import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../shared/CustomButton";
import Spinner from "../../../shared/Spinner";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import { paymentMethods, cardLabels, getCardOptions, getCardCost } from "../../../constants/VisitsConstants";
import { FaCheckDouble, FaMoneyBill, FaPlus, FaTrash } from "react-icons/fa";
import { fetchData } from "../../../helpers/fetchData";
import CheckPermission from "../../../hooks/CheckPermission";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { practionerDepartmentOptions } from '../../../constants/UserConstants';
import moment from "moment";
import DatePicker from "../../../shared/DatePicker";

const DepositForm = ({
  selectedPatient,
  setOpenDepositForm,
  withOutSlot = false,
  state,
  onDepositSuccess,
  prevPath,
  onPrint,
}) => {
  const { hasPermission } = CheckPermission();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [cardOptions, setCardOptions] = useState([]);
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const initialValues = {
    department_id: '',
    deposit_date: new Date(),
    patient_id: selectedPatient?.patient_id || "",
    deposit_amount: "",
    total_deposit_vat: 0,
    depositor_name: selectedPatient?.full_name || "",
    depositor_contact: selectedPatient?.contact || "",
    deposit_remarks: "",
    reference: "",
    payment_option: "card",
    cardType: "Mada",
    cash: null,
    bank: null,
    Mada: null,
    cards: [{ cardType: "Mada", amount: "" }],
    temp_add_card: null,
  };

  const validationSchema = Yup.object().shape({
    total_deposit_vat: Yup.number().nullable(),
    deposit_date: Yup.date()
      .required(t("Deposit date is required"))
      .max(new Date(), t("Deposit date cannot be in the future")),
    deposit_amount: Yup.number()
      .required(t("Deposit amount is required"))
      .min(0, t("Must be positive or zero")),
    depositor_name: Yup.string().required(t("Depositor name is required")),
    department_id: Yup.string().required(t("Department is required")),
    depositor_contact: Yup.string().required(t("Contact is required")),
    deposit_remarks: Yup.string(),
    payment_option: Yup.string().required(t("Payment option is required")),
    cash: Yup.number().nullable().when("payment_option", {
      is: "multi_payment",
      then: (schema) => schema.required(t("Cash amount is required")).min(0, t("Must be positive or zero")),
      otherwise: (schema) => schema.nullable(),
    }),
    bank: Yup.number().nullable().when("payment_option", {
      is: "multi_payment",
      then: (schema) => schema.required(t("Bank amount is required")).min(0, t("Must be positive or zero")),
      otherwise: (schema) => schema.nullable(),
    }),
  }).test(
    "payment-total-validation",
    t("Payment methods total cannot exceed deposit amount"),
    function (values) {
      if (values.payment_option === "multi_payment") {
        const depositAmount = parseFloat(values.deposit_amount) || 0;
        const cash = parseFloat(values.cash) || 0;
        const bank = parseFloat(values.bank) || 0;

        const cardsTotal = values.cards?.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0) || 0;
        const total = cash + bank + cardsTotal;

        if (Math.abs(total - depositAmount) > 0.05) {
          return this.createError({
            path: 'cash',
            message: t('Total payment must match the deposit amount'),
          });
        }
      }
      return true;
    }
  );

  const handleSubmit = async (values) => {
    if (!hasPermission("deposit", "create")) return;
    if (values?.payment_option === "card" || values?.payment_option === "multi_payment") {
      if (values?.payment_option === "card") {
        const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
          Array.isArray(group.options) && group.options.some(opt => opt.value === values.cardType || opt.label === values.cardType)
        );
        if (!values.cardType || !isValid) {
          toast.error(t("Please Select Card Type"));
          return;
        }
      } else if (values?.payment_option === "multi_payment") {
        const cardPayments = values.cards?.filter(card => Number(card.amount) > 0) || [];
        for (const card of cardPayments) {
          const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
            Array.isArray(group.options) && group.options.some(opt => opt.value === card.cardType || opt.label === card.cardType)
          );
          if (!card.cardType || !isValid) {
            toast.error(t("Please Select Card Type for all card payments"));
            return;
          }
        }
      }
    }

    const payload = {
      patient_id: values.patient_id,
      file_number:selectedPatient?.file_no,
      document_id:selectedPatient?.document_id,
      department_id: values.department_id,
      deposit_detail: {
        deposit_date: moment(values?.deposit_date)
          .hour(moment().hour())
          .minute(moment().minute())
          .second(moment().second())
          .format("YYYY-MM-DD HH:mm:ss"),
        reference: values?.reference,
        deposit_amount: Number((Number(values.deposit_amount || 0) - Number(values.total_deposit_vat || 0)).toFixed(2)),
        depositor_name: values.depositor_name,
        total_deposit_vat: values.total_deposit_vat,
        total_amount_with_vat: Number(values.deposit_amount),
        depositor_contact: values.depositor_contact,
        deposit_remarks: values.deposit_remarks,
        payment_option: values.payment_option,
        ...(values.payment_option === "multi_payment" && {
          payment_detail: [
            ...(Number(values?.cash) > 0
              ? [{
                payment_method: 'cash',
                amount: values.cash,
              }]
              : []),

            ...(Number(values?.bank) > 0
              ? [{
                payment_method: 'bank',
                amount: values.bank,
              }]
              : []),

            ...(Number(values?.credit) > 0
              ? [{
                payment_method: 'credit',
                amount: values.credit,
              }]
              : []),
            ...(values.cards
              ?.filter(card => Number(card.amount) > 0)
              ?.map(card => ({
                payment_method: card.cardType,
                amount: card.amount,
                card_cost: getCardCost(cardOptions, card.cardType, card.amount),
              })) || [])
          ]
        }),
        ...(values.payment_option === "card" && {
          payment_detail: [
            {
              payment_method: values?.cardType,
              amount: Number(values.deposit_amount),
              card_cost: getCardCost(cardOptions, values?.cardType, Number(values.deposit_amount))
            },
          ],
        }),
      },
    };

    if (payload?.deposit_detail?.payment_option === "multi_payment") {
      const totalPayment = payload?.deposit_detail?.payment_detail?.reduce((acc, curr) => acc + Number(curr?.amount || 0), 0);
      if (Math.abs(totalPayment - Number(payload?.deposit_detail?.total_amount_with_vat)) > 0.05) {
        toast.error(t("Total payment must be equal to deposit amount"));
        return;
      }
    }

    setLoading(true);

    try {
      const res = await fetchData("POST", "/v3/deposit/create", payload);
      if (res) {
        toast.success(res?.message);
        const depositInvoiceNumber = res?.deposit_invoice_number || res?.invoice_number;
        if (onDepositSuccess) {
          onDepositSuccess();
        }
        if (depositInvoiceNumber) {
          if (onPrint) {
            onPrint(depositInvoiceNumber);
            setOpenDepositForm(false);
          } else {
            navigate(`/print-invoice/${res?.invoice_number}`, {
              state: {
                ...state,
                invoiceNumber: depositInvoiceNumber,
                deposit: true,
                navigattion: prevPath || (withOutSlot ? "/create-visit-without-slot" : "/create-visit"),
                patient: selectedPatient,
              },
            });
          }
        } else {
          setOpenDepositForm(false);
        }
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };


  const fetchDepartments = async () => {
    try {
      const response = await fetchData('POST', '/v3/department/getList', {});
      if (response?.data) setDepartments(practionerDepartmentOptions(response.data));
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  useEffect(() => {
    fetchDepartments()
  }, [])


  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DropdownField
                filter
                label={t('Select Department')}
                options={departments}
                name='department_id'
                optionLabel='label'
                optionValue='value'
                placeholder={t('Select Department')}
                className='w-full'
                onChange={(e) => {
                  const value = e.value;
                  props.setFieldValue('department_id', e.value);
                  if (value) {
                    const payload = {
                      department: {
                        id: value
                      }
                    }
                    getCardOptions(setCardOptions, payload)
                  }

                }}
              />
              <DatePicker
                label={t("Deposit Date")}
                name="deposit_date"
                dateFormat="dd/mm/yy"
                placeholder={t("Select deposit date")}
                maxDate={new Date()}
              />
              <TextInput
                label={t("Depositor Name")}
                name="depositor_name"
                placeholder={t("Depositor Name")}
                readOnly
              />

              <TextInput
                label={t("Depositor Contact")}
                name="depositor_contact"
                placeholder={t("eg: 1234567890 or +977-1234567890")}
                readOnly
              />
              <DropdownField
                label={t("Payment Options")}
                options={paymentMethods(t)}
                placeholder={t("Select Payment Method")}
                name="payment_option"
                onChange={(e) => {
                  const method = e.value;
                  const currentCardKey = props.values.cardType || "Mada";

                  props.setFieldValue("payment_option", method);
                  if (method === "card") {
                    props.setValues((prev) => ({
                      ...prev,
                      payment_option: method,
                      cardType: currentCardKey,
                      cash: null,
                      bank: null,
                    }));
                  } else if (method === "multi_payment") {
                    props.setValues((prev) => ({
                      ...prev,
                      payment_option: method,
                      cardType: prev.cardType || "Mada",
                      cash: prev.cash ?? 0,
                      bank: prev.bank ?? 0,
                      cards: (prev.cards && prev.cards.length > 0) ? prev.cards : [{ cardType: "Mada", amount: "" }],
                    }));
                  } else {
                    props.setValues((prev) => ({
                      ...prev,
                      payment_option: method,
                      cardType: "",
                      cash: null,
                      bank: null,
                      cards: [],
                    }));
                  }
                }}
              />
              {(props?.values?.payment_option === "card" ||
                props?.values?.payment_option === "multi_payment") && (
                  <DropdownField
                    label={props?.values?.payment_option === "multi_payment" ? t("Add Card") : t("Card Type")}
                    placeholder={t("SelectCardType")}
                    name="cardType"
                    value={props?.values?.payment_option === "multi_payment" ? null : props.values.cardType}
                    options={props?.values?.payment_option === "multi_payment"
                      ? cardOptions.map(group => ({
                        ...group,
                        options: group?.options?.filter(opt =>
                          !props.values.cards?.some(card => card.cardType === opt.value)
                        )
                      })).filter(group => group.options && group.options.length > 0)
                      : cardOptions
                    }
                    onChange={(e) => {
                      if (props.values.payment_option === "multi_payment") {
                        if (e.value) {
                          const newCards = [...(props.values.cards || []), { cardType: e.value, amount: "" }];
                          props.setFieldValue('cards', newCards);
                          setTimeout(() => props.setFieldValue('cardType', null), 0);
                        }
                      } else {
                        props.setFieldValue('cardType', e.value);
                      }
                    }}
                  />
                )}

              {props?.values?.payment_option === "multi_payment" && (
                <>
                  <TextInput
                    name="cash"
                    label={t("Cash")}
                    placeholder={t("EnterCashAmount")}
                    type="number"
                    step="any"
                  />
                  <TextInput
                    name="bank"
                    label={t("Bank")}
                    placeholder={t("EnterBankAmount")}
                    type="number"
                    step="any"
                  />

                  {props.values.cards && props.values.cards.map((card, index) => (
                    <div key={index} className="col-span-1 flex items-end gap-2">
                      <div className="flex-1">
                        <TextInput
                          name={`cards.${index}.amount`}
                          label={cardLabels[card.cardType] || card.cardType}
                          placeholder={t("Amount")}
                          type="number"
                          step="any"
                        />
                      </div>
                      <div className="mb-1">
                        <button
                          type="button"
                          onClick={() => {
                            const newCards = props.values.cards.filter((_, i) => i !== index);
                            props.setFieldValue('cards', newCards);
                          }}
                          className="text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                          title={t("Remove")}
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
              <TextInput
                label={t("Reference")}
                name="reference"
                placeholder={t("Enter reference")}
              />
              <TextInput
                label={t("Deposit Remarks")}
                name="deposit_remarks"
                placeholder={t("Deposit Remark")}
              />
              <TextInput
                label={t("Deposit Amount")}
                name="deposit_amount"
                placeholder="0"
                type="number"
                step="any"
                onChange={(e) => {
                  const value = e.target.value;
                  props.setFieldValue("deposit_amount", value);
                  const saudiPatient = selectedPatient?.nationality?.toLowerCase() === "saudi";
                  const valNum = Number(value) || 0;
                  const vat = (!saudiPatient && valNum > 0)
                    ? Number((valNum - (valNum / 1.15)).toFixed(2))
                    : 0;
                  props.setFieldValue("total_deposit_vat", vat);
                }}
              />
            </div>
            <div className='w-full flex items-center justify-end'>
              <div className='bg-gray-100 p-2 rounded-md mt-3 w-[300px] flex flex-col gap-3'>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray !font-[600]'>{t('Deposit Amount')} </h6>
                  <p className='text-14-dark'>
                    {(Number(props.values.deposit_amount || 0) - Number(props.values.total_deposit_vat || 0)).toFixed(2)}
                  </p>
                </div>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray !font-[600]'>{t('Total Vat')} </h6>
                  <p className='text-14-dark'>
                    {Number(props.values.total_deposit_vat || 0).toFixed(2)}
                  </p>
                </div>
                <div className='flex justify-between items-center my-1'>
                  <h6 className='text-14-gray !font-[600]'>{t('Total Deposit Amount With Vat')} </h6>
                  <p className='text-14-dark'>
                    {Number(props.values.deposit_amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-2 flex justify-end items-center">
              {hasPermission("deposit", "create") && <CustomButton
                type="submit"
                icon={FaCheckDouble}
                label={t("Submit")}
                loading={loading}
                buttonType="primary"
              />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default DepositForm;

